import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ActivityCard,
  AiMatchAnalyticsCard,
  MatchListing,
  StatsCard,
} from '../../components/dashboard';
import Spinner from '../../components/common/Spinner.tsx';
import {
  useDashboardAiMatchAnalytics,
  useDashboardRecentActivity,
  useDashboardStats,
} from '../../hooks/useDashboard.ts';
import { useOnboardingMatches } from '../../hooks/useMatch.ts';
import {
  aiMatchAnalytics as dashboardAiMatchAnalytics,
  dashboardCards,
} from '../../lib/constants.tsx';
import { routes } from '../../lib/routes';
import { getAiMatchAnalyticsColors } from '../../utils';
import type {
  ActivityItem,
  AiMatchAnalyticsItem,
  StatsCardItem,
} from '../../types/dashboard.ts';
import { MdOutlineArrowForward } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { Tour } from '../../components/common/Tour';
import { Tooltip } from '@radix-ui/themes';
import { InfoCircle } from 'iconoir-react';
import { InfoTip } from '../../components/common/InfoTip';

// Key used to prevent onboarding restart race condition
const STORAGE_KEY_JUST_COMPLETED = 'onboarding_just_completed';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // V1.5: white-label brand name for the empty-state copy, resolved dynamically
  // per event/host (never hardcoded). `isDefault` distinguishes a client brand
  // from the 2Connect default so the copy reads naturally either way.
  const { name: brandName, isDefault } = useBranding();

  // Clear the onboarding completion flag when dashboard loads successfully
  // This prevents the race condition where the flag persists indefinitely
  useEffect(() => {
    const justCompleted = sessionStorage.getItem(STORAGE_KEY_JUST_COMPLETED);
    if (justCompleted) {
      sessionStorage.removeItem(STORAGE_KEY_JUST_COMPLETED);
    }
  }, []);

  const [dashboardStats, setDashboardStats] = useState<StatsCardItem[]>([]);
  const [aiMatchAnalytics, setAiMatchAnalytics] = useState<
    AiMatchAnalyticsItem[]
  >([]);
  const { data: dashboardStatsData, isLoading: isStatsLoading } =
    useDashboardStats();
  const {
    data: dashboardAiMatchAnalyticsData,
    isLoading: isAiMatchAnalyticsLoading,
  } = useDashboardAiMatchAnalytics();
  const {
    data: dashboardRecentActivityData,
    isLoading: isRecentActivityLoading,
  } = useDashboardRecentActivity();
  const { data: onboardingMatchesData, isLoading } = useOnboardingMatches();

  // Limit matches shown on mobile dashboard (show 3), all on desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dashboardMatches = useMemo(() => {
    const all = onboardingMatchesData?.result?.matches || [];
    return isMobile ? all.slice(0, 3) : all;
  }, [onboardingMatchesData, isMobile]);

  // Populate dashboard cards: Pending Review, Connections, Conversations, High Compatibility
  useEffect(() => {
    if (dashboardStatsData && !isStatsLoading) {
      const { pending_review, approved_matches, conversations, high_compatibility } = dashboardStatsData.result;

      const updatedCards = dashboardCards.map((card, index) => {
        const values = [pending_review ?? 0, approved_matches, conversations, high_compatibility];
        return { ...card, value: values[index] };
      });

      setDashboardStats(updatedCards);
    }
  }, [dashboardStatsData, isStatsLoading]);

  // Match Quality section. Mirrors the Matches page, which builds this the
  // same way.
  //
  // The previous version wrote the percentages into the imported constant and
  // then passed that same array to setState. Two problems: React compares by
  // reference, so once the state already held that array no later fetch could
  // re-render, and the constant is shared with the Matches page, so this page
  // was editing the other page's defaults. Build a new array instead.
  //
  // avg_match_score is null when nothing has been AI-scored yet. It is passed
  // through rather than coerced, so the card reads N/A instead of a zero the
  // user would take as a real score. Rates are genuine numbers and default 0.
  useEffect(() => {
    if (dashboardAiMatchAnalyticsData && !isAiMatchAnalyticsLoading) {
      const { avg_match_score, response_rate, connection_rate } = dashboardAiMatchAnalyticsData.result;
      const values = [avg_match_score ?? null, response_rate ?? 0, connection_rate ?? 0];
      setAiMatchAnalytics(
        dashboardAiMatchAnalytics.map((card, index) => ({
          ...card,
          percentage: values[index],
        })),
      );
    }
  }, [dashboardAiMatchAnalyticsData, isAiMatchAnalyticsLoading]);

  return (
    <div className='px-4 py-6'>
      <Tour name='dashboard' />
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h3 className='!font-medium text-heading-500 mb-1'>Dashboard</h3>
          <p className='text-sm text-body-400 font-medium mb-0'>
            Welcome{user?.first_name ? `, ${user.first_name}` : ''}! Here's your networking overview.
          </p>
        </div>
      </div>

      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6'>
        {isStatsLoading && (
          <div className='col-span-4 flex items-center justify-center my-13'>
            <Spinner size='md' color='primary' />
          </div>
        )}
        {dashboardStats.length > 0 &&
          dashboardStats.map((item, index) => {
            // Jun-16 (a user V1 test, D4): the "High compatibility" tile was a
            // dead number; make it navigate to the matches list.
            const onClick =
              item.title === 'High compatibility'
                ? () => navigate(routes.matches)
                : undefined;
            return <StatsCard item={item} key={index} onClick={onClick} />;
          })}
        {/* Empty state when all stats are zero — only show for users who haven't completed onboarding */}
        {!isStatsLoading && dashboardStats.length > 0 &&
          dashboardStats.every(s => s.value === 0) &&
          user?.onboarding_status !== 'completed' && (
            <div
              className='col-span-4 bg-gradient-to-r from-primary-50 to-purple-50 rounded-xl p-6 text-center cursor-pointer hover:shadow-md transition-shadow'
              onClick={() => navigate(routes.settings)}
            >
              <p className='text-heading-500 !font-medium mb-2'>Your journey starts here!</p>
              <p className='text-sm text-body-400 mb-0'>
                Your AI agent is getting ready to find you great matches. Complete your profile to speed things up!
              </p>
            </div>
          )}
      </div>

      {/* V1.5 B2B2C (2026-06-08): the events-browsing section is HIDDEN.
          Users now belong to exactly one event (joined via an organiser's
          invite link) and must NOT see or browse other events. Discover +
          Matches are already scoped to the user's event pool. The
          EventsSection / EventCard / EventDetailModal components are retained
          (not deleted) for an easy revert. Was: <EventsSection /> */}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {dashboardMatches.length > 0 ? (
        <div>
          <div>
            <h6 className='text-heading-500 !font-medium mb-2'>
              Match quality
            </h6>
            <p className='text-sm text-body-400 font-medium mb-4'>
              Your matching performance metrics:
            </p>
          </div>

          <div className='grid grid-cols-3 gap-3 sm:gap-6 mb-4'>
            {isAiMatchAnalyticsLoading && (
              <div className='col-span-3 flex items-center justify-center my-8'>
                <Spinner size='md' color='primary' />
              </div>
            )}
            {aiMatchAnalytics.length > 0 &&
              aiMatchAnalytics.map((item, index) => {
                const { bg = '', text = '' } =
                  getAiMatchAnalyticsColors(index) || {};

                return (
                  <AiMatchAnalyticsCard
                    key={index}
                    item={item}
                    bg={bg}
                    text={text}
                    isLast={false}
                  />
                );
              })}
          </div>

          {/* Match tier breakdown */}
          {dashboardStatsData?.result?.match_tier_breakdown && (
            <div className='mb-6 p-4 bg-gray-50 rounded-xl'>
              {/* Apr-25: InfoTip next to header explaining the tier
                  thresholds so users can read the bars meaningfully. */}
              <h6 className='text-xs font-semibold text-body-500 mb-3 uppercase tracking-wide inline-flex items-center gap-1.5'>
                Match Distribution
                <Tooltip content='How your matches break down by strength. Perfect and Strong are your closest fits, Worth Exploring is a partial fit, and Low is a weak fit.'>
                  <button
                    type='button'
                    className='inline-flex items-center justify-center focus:outline-none'
                    aria-label='About Match Distribution'
                  >
                    <InfoCircle className='w-3.5 h-3.5 text-body-300 hover:text-body-500 transition-colors' />
                  </button>
                </Tooltip>
              </h6>
              <div className='space-y-2'>
                {[
                  { label: 'Perfect', key: 'perfect', color: 'bg-green-500' },
                  { label: 'Strong', key: 'strong', color: 'bg-primary-500' },
                  { label: 'Worth exploring', key: 'worth_exploring', color: 'bg-yellow-500' },
                  { label: 'Low', key: 'low', color: 'bg-gray-300' },
                ].map(tier => {
                  const count = dashboardStatsData.result.match_tier_breakdown[tier.key] || 0;
                  const total = dashboardStatsData.result.total_matches || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={tier.key} className='flex items-center gap-3'>
                      <span className='text-xs text-body-400 w-28 shrink-0'>{tier.label}</span>
                      <div className='flex-1 h-2 bg-gray-200 rounded-full overflow-hidden'>
                        <div className={`h-full ${tier.color} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className='text-xs font-semibold text-body-500 w-8 text-right'>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Engagement level */}
          {dashboardAiMatchAnalyticsData?.result?.activity_level && (
            <div className='mb-6 p-4 bg-gradient-to-r from-primary-25 to-purple-50 rounded-xl flex items-center justify-between'>
              <div>
                <h6 className='text-xs font-semibold text-body-500 uppercase tracking-wide inline-flex items-center gap-1'>
                  Your Engagement
                  <InfoTip tooltipKey='engagement-score' />
                </h6>
                <span className='text-lg font-bold text-primary-500 capitalize block'>
                  {dashboardAiMatchAnalyticsData.result.activity_level.replace('_', ' ')}
                </span>
              </div>
              <div className='text-right'>
                <span className='text-2xl font-bold text-primary-500'>
                  {Math.round(dashboardAiMatchAnalyticsData.result.engagement_score || 0)}
                </span>
                <span className='text-xs text-body-400 block'>/ 100</span>
              </div>
            </div>
          )}
        </div>
        ) : (
        <div>
          <div className='p-6 bg-gradient-to-r from-primary-25 to-purple-50 rounded-xl text-center'>
            <p className='text-heading-500 !font-medium mb-2'>
              {isDefault
                ? 'Your agent is finding your matches'
                : `You're all set on ${brandName}`}
            </p>
            <p className='text-sm text-body-400 mb-4'>
              Your AI agent is live and working for you. New connections appear
              here as the right people join{isDefault ? '' : ` ${brandName}`}, and
              we'll notify you the moment one lands. A more complete profile means
              better matches.
            </p>
            <button
              type='button'
              onClick={() => navigate('/profile')}
              className='inline-flex items-center px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors'
            >
              Strengthen your profile
            </button>
          </div>
        </div>
        )}

        <div>
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h6 className='text-heading-500 !font-medium mb-1'>
                Matches
              </h6>
              <p className='text-sm text-body-400 font-medium mb-0'>
                Matches your AI agent found for you, review and connect with the ones you like.
              </p>
            </div>
            {/* A real link, so it is keyboard reachable and supports
                ctrl/middle click like any other navigation on the page. */}
            <Link
              to={routes.matches}
              className='flex items-center text-body-400 px-3 py-1 bg-gray-100 w-fit rounded-md cursor-pointer'
            >
              <span className='text-xs leading-4 font-semibold px-2'>
                View All
              </span>{' '}
              <MdOutlineArrowForward size={24} />
            </Link>
          </div>

          <div className='h-full max-h-[35rem] overflow-y-auto'>
            {dashboardMatches.length < 1 &&
            !isLoading &&
            onboardingMatchesData?.result?.matches_found ? (
              <div className='flex items-center justify-center py-12'>
                <div className='text-center'>
                  <div className='text-heading-500 !font-medium mb-2'>
                    You're up to date! 🚀
                  </div>
                  <div className='text-sm text-body-400 px-3 sm:max-w-[70%] mx-auto'>
                    You've reviewed all your matches for now. Your AI agent is
                    scanning your network for the next great introduction. Check
                    back soon!
                  </div>
                </div>
              </div>
            ) : (
              <MatchListing
                wrapperClassName='!grid-cols-1 !max-h-full overflow-y-none'
                matches={dashboardMatches}
                currentPage={1}
                totalPages={1}
                totalItems={dashboardMatches.length}
                limit={dashboardMatches.length}
                loading={isLoading}
                onPageChange={() => {}}
                variant='horizontal'
                allowPagination={false}
                isFindingMatches={(() => {
                  const startedAt = sessionStorage.getItem('finding_matches_started_at');
                  if (!startedAt) return false;
                  const elapsed = Date.now() - parseInt(startedAt, 10);
                  return elapsed < 120000; // 2 min timeout
                })()}
                emptyMessage={isDefault
                  ? 'No matches yet. Your agent will surface connections as the right people join, and you will be notified.'
                  : `No matches yet. As more people join ${brandName}, your agent surfaces the right connections for you, and you will be notified.`}
              />
            )}
          </div>

          <div className='mt-6'>
          <h6 className='text-heading-500 !font-medium mb-6'>
            Recent agent activity
          </h6>

          <div className='h-full max-h-[49.5rem] overflow-y-auto'>
            {isRecentActivityLoading && (
              <div className='col-span-4 flex items-center justify-center my-8'>
                <Spinner size='md' color='primary' />
              </div>
            )}
            {dashboardRecentActivityData?.result?.items?.length > 0 &&
              dashboardRecentActivityData?.result?.items?.map(
                (activity: ActivityItem, index: number) => (
                  <ActivityCard activity={activity} key={index} />
                )
              )}
            {/* BUG-016 FIX: Empty state for recent activity */}
            {!isRecentActivityLoading && (!dashboardRecentActivityData?.result?.items || dashboardRecentActivityData?.result?.items?.length === 0) && (
              <div className='flex items-center justify-center py-12 bg-gray-50 rounded-xl'>
                <div className='text-center'>
                  <div className='text-4xl mb-3'>🤖</div>
                  <div className='text-heading-500 !font-medium mb-2'>
                    No activity yet
                  </div>
                  <div className='text-sm text-body-400 px-4'>
                    Your AI agent will log activity here as it works to find your best matches.
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HomePage;
